import React, { createContext, useContext, useState, useCallback, useRef } from 'react';

const ChartOverlayContext = createContext();

import PropTypes from 'prop-types';
import { deleteLastOverlay as popLastOverlay, newOverlayGroupId } from '../utils/drawingStack';

export function ChartOverlayProvider(props) {
  const { children } = props;
  // Always store overlays as a flat array
  const [overlays, setOverlays] = useState([]);

  /**
   * The batch currently being filled, so one action's overlays share a groupId and undo removes
   * them together.
   *
   * Stamped HERE rather than in each study dialog, and that is the whole point. A study run adds
   * its overlays one `addOverlay` call at a time in a synchronous loop — `overlays.forEach(ov =>
   * addOverlay(ov))` — spread across a dozen dialogs. Requiring each of them to open and close a
   * batch would mean editing every one, and any dialog that forgot would produce a study whose
   * bands delete one at a time. Batching by arrival time needs no cooperation from callers and
   * cannot be forgotten by a new one.
   *
   * The window closes on a macrotask rather than a microtask deliberately: a study that awaits
   * between adds resolves those awaits as microtasks, so a microtask window would split one study
   * across several batches. Separate user actions are always separate macrotasks, so nothing
   * unrelated gets merged.
   */
  const batchRef = useRef(null);

  const currentBatchId = useCallback(() => {
    if (batchRef.current === null) {
      batchRef.current = newOverlayGroupId('study');
      setTimeout(() => { batchRef.current = null; }, 0);
    }
    return batchRef.current;
  }, []);

  // Every setter below updates through the functional form, so none of them depends on anything
  // outside setOverlays (which useState guarantees is stable). Wrapping them in useCallback with
  // an empty dependency list therefore changes no behaviour — it only stops each render handing
  // consumers brand-new function identities, which would otherwise force callers that legitimately
  // list them as hook dependencies to rebuild on every render.
  const addOverlay = useCallback((overlay) => {
    console.log('[OverlayContext] Adding overlay:', overlay);
    // Anything that already carries a groupId keeps it — a drawing tool has already decided which
    // figure its lines belong to, and that must win over arrival time.
    const batchId = currentBatchId();
    const stamp = (o) => (o && !o.groupId ? { ...o, groupId: batchId } : o);
    setOverlays((prev) => {
      // Always flatten: if overlay is an array, spread it; else push as single
      let updated;
      if (Array.isArray(overlay)) {
        updated = [...prev, ...overlay.map(stamp)];
      } else {
        updated = [...prev, stamp(overlay)];
      }
      console.log('[OverlayContext] Overlays after add:', updated);
      return updated;
    });
  }, [currentBatchId]);

  // Batch set overlays
  const setAllOverlays = useCallback((overlayArray) => {
    console.log('[OverlayContext] Setting all overlays (batch):', overlayArray);
    // Always flatten overlayArray
    const flat = overlayArray.flat ? overlayArray.flat() : overlayArray;
    setOverlays(flat);
  }, []);

  // Clear all overlays
  const clearOverlays = useCallback(() => {
    console.log('[OverlayContext] Clearing all overlays');
    setOverlays([]);
  }, []);

  // Remove the most recently added figure — a drawn one or a whole study run. See
  // utils/drawingStack for why the unit of undo is an action rather than a single overlay.
  const deleteLastOverlay = useCallback(() => {
    setOverlays((prev) => popLastOverlay(prev));
  }, []);

  // Remove a single overlay by id
  const removeOverlay = useCallback((id) => {
    console.log('[OverlayContext] Removing overlay with id:', id);
    setOverlays((prev) => {
      const updated = prev.filter((o) => o.id !== id);
      console.log('[OverlayContext] Overlays after remove:', updated);
      return updated;
    });
  }, []);

  // Update overlay position by id
  const updateOverlayPosition = useCallback((id, position) => {
    setOverlays((prev) => prev.map(o => o.id === id ? { ...o, position } : o));
  }, []);

  // Update arbitrary fields on an overlay by id
  const updateOverlay = useCallback((id, fields) => {
    setOverlays((prev) => prev.map(o => o.id === id ? { ...o, ...fields } : o));
  }, []);

  // Always provide overlays as a flat array
  const overlaysFlat = overlays.flat ? overlays.flat() : overlays;
  console.log('[OverlayContext] overlays provided to children:', overlaysFlat);
  return (
    <ChartOverlayContext.Provider value={{ overlays, addOverlay, setAllOverlays, clearOverlays, deleteLastOverlay, deleteLastDrawing: deleteLastOverlay, removeOverlay, updateOverlayPosition, updateOverlay }}>
      {children}
    </ChartOverlayContext.Provider>
  );
}

ChartOverlayProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export function useChartOverlays() {
  return useContext(ChartOverlayContext);
}
